import { AlertTriangle, ImageIcon } from 'lucide-react'

import { bodyImages } from '@/components/blog/draftImages'

const field =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'

/**
 * Alt text for every image in the post body.
 *
 * The alt text is already in the Markdown — `![this bit](/blog-images/x.png)` —
 * so this edits that text in place rather than storing anything alongside it.
 * Listing the images instead of putting a field under each one in the preview is
 * deliberate: two references to the same file are two separate images with two
 * separate descriptions, and only their order in the body tells them apart.
 *
 * Empty is a legitimate answer. An image that carries no information a reader
 * would miss is decorative, and `alt=""` is the correct way to say so — hence a
 * hint rather than a validation error.
 */
export default function ImageAltText({
  body,
  onChange,
  resolveImage,
}: {
  body: string
  /** Called with the image's position in the body and its new description. */
  onChange: (index: number, alt: string) => void
  /** Maps a body path to a preview data URI for images not committed yet. */
  resolveImage?: (src: string) => string | undefined
}) {
  const images = bodyImages(body)
  if (!images.length) return null

  const missing = images.filter(image => !image.alt.trim()).length

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Image alt text</h2>
        <span className={`text-xs ${missing ? 'text-amber-700' : 'text-gray-400'}`}>
          {missing ? `${missing} without a description` : 'all described'}
        </span>
      </div>

      <div className="space-y-3">
        {images.map(image => {
          // Only images attached to this draft can be shown: one already in the
          // repo is served by the website, not by the admin panel, so its path
          // would 404 here and render as a broken image.
          const src = resolveImage?.(image.url)
          return (
            <div key={image.index} className="flex gap-3">
              {src ? (
                <img
                  src={src}
                  alt=""
                  className="h-12 w-16 shrink-0 rounded border border-gray-200 bg-gray-50 object-cover"
                />
              ) : (
                <span
                  className="flex h-12 w-16 shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-300"
                  title="Already published — shown on the site, not here"
                >
                  <ImageIcon className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <input
                  className={field}
                  value={image.alt}
                  placeholder="Describe what the image shows"
                  aria-label={`Alt text for ${image.url.split('/').pop()}`}
                  onChange={event => onChange(image.index, event.target.value)}
                />
                <p className="mt-1 truncate font-mono text-xs text-gray-400">{image.url}</p>
              </div>
            </div>
          )
        })}
      </div>

      {missing > 0 && (
        <p className="mt-3 flex items-start gap-2 text-xs text-amber-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            A screen reader announces nothing for an image with no alt text, and search engines have
            nothing to index. Leave it empty only if the image is purely decorative.
          </span>
        </p>
      )}
    </div>
  )
}
